import React from 'react';
import { ButtonV2, Flex, useDisclosure } from '@withjoy/joykit';
import { HotelTileList } from '../../components/Hotel';
import { HotelMap, HotelMapSkeleton } from '../../components/Hotel/HotelMap';
import { useGuestEventHotelRoomBlocksQuery } from '@graphql/generated';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { pxToRem } from '@withjoy/joykit/theme';
import { MapMarker } from '@withjoy/joykit/icons';
import { useMediaQuery } from '@withjoy/joykit/utils';
import { useCustomPageTelemetry } from './Custom.telemetry';
import { SkeletonGroup } from '@shared/components/Skeleton';
import { HotelTileSkeleton } from '../../components/Hotel/HotelTile';

interface Props {
  pageId: string;
  eventId: string;
  eventHandle: string;
  pageTitle?: string;
  subTitle?: string | null;
  isBrannanLayout?: boolean;
  isHotelCoupleNoteOpen?: boolean;
  handleCoupleNoteOpen?: () => void;
}

export const Custom: React.FC<Props> = ({ pageId, eventId, eventHandle, pageTitle, subTitle, isHotelCoupleNoteOpen, isBrannanLayout, handleCoupleNoteOpen }) => {
  const telemetry = useCustomPageTelemetry();
  const isMobile = useMediaQuery(theme => theme.mediaQueries.between(0, { breakpointAlias: 'sm2' }));
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { data, loading } = useGuestEventHotelRoomBlocksQuery({ variables: { eventId }, batchMode: 'fast' });
  const destination = data?.event?.hotelRoomBlocks?.destination || {};
  const reservedRoomBlocks = data?.event?.hotelRoomBlocks?.reservedRoomBlocks;
  const eventPhoto = data?.event?.photo?.url ?? '';

  const handleScrollToTopOnClose = useEventCallback(() => {
    onClose();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  });

  const onToggleFullscreen = useEventCallback((fullscreen: boolean) => {
    if (isBrannanLayout) {
      fullscreen ? onOpen() : handleScrollToTopOnClose();
    }
  });

  const onCloseMap = useEventCallback(() => {
    onClose();
    telemetry.hotelListDrawerOpened('accommodations');
  });

  const onOpenMap = useEventCallback(() => {
    onOpen();
    telemetry.mapExpanded('accommodations');
  });

  const onSelectHotel = useEventCallback((id: string) => {
    if (isBrannanLayout) {
      onCloseMap();
    }
    // When click on marker, wait to open drawer first and then scroll to card
    setTimeout(() => {
      const scrollEl = document.getElementById(id);
      if (scrollEl) {
        const elementTop = scrollEl.getBoundingClientRect().top;
        const offsetPosition = elementTop + window.scrollY - (isBrannanLayout && !isMobile ? 32 : 0);
        // To support scrolling to card in Brannan with offset and Aloha without offset
        isBrannanLayout ? window.scrollTo({ top: offsetPosition, behavior: 'smooth' }) : scrollEl.scrollIntoView({ behavior: 'smooth' });
      }
    }, 300);
  });

  return (
    <Flex
      flexDirection={isBrannanLayout && !isMobile ? 'row' : 'column'}
      justifyContent={'center'}
      alignItems={isBrannanLayout ? 'flex-start' : 'center'}
      width={'100%'}
      paddingY={{ _: 0, sm2: isBrannanLayout ? 7 : 0 }}
      paddingRight={{ _: 0, sm2: isBrannanLayout ? 7 : 0 }}
      paddingLeft={{ _: 0, sm2: isBrannanLayout ? 0 : 0 }}
    >
      <Flex
        width={isBrannanLayout && !isMobile ? pxToRem(400) : '100%'}
        flex={isBrannanLayout ? 'none' : 'auto'}
        flexDirection={'column'}
        paddingLeft={isBrannanLayout ? (isMobile ? 6 : 7) : 0}
        paddingRight={isBrannanLayout ? (isMobile ? 6 : 7) : 0}
        position={{ sm2: isBrannanLayout ? 'sticky' : 'initial' }}
        top={{ sm2: isBrannanLayout ? 7 : 0 }}
      >
        <SkeletonGroup isReady={!loading} placeholder={<HotelTileSkeleton />}>
          {reservedRoomBlocks && (
            <HotelTileList
              hotelTileListData={reservedRoomBlocks}
              destination={destination}
              eventPhoto={eventPhoto}
              eventHandle={eventHandle}
              handleCoupleNoteOpen={handleCoupleNoteOpen}
              isCoupleNoteOpen={isHotelCoupleNoteOpen}
            />
          )}
        </SkeletonGroup>
      </Flex>
      <SkeletonGroup
        isReady={!loading}
        placeholder={<HotelMapSkeleton isBrannanLayout={isBrannanLayout} />}
        width="100%"
        position={{ sm2: isBrannanLayout ? 'sticky' : 'initial' }}
        top={{ sm2: isBrannanLayout ? 7 : 0 }}
      >
        {/* Visible to Aloha Desktop & Mobile and Brannan Desktop */}
        {destination && reservedRoomBlocks && (!isBrannanLayout || !isMobile || isOpen) && (
          <HotelMap
            showFullScreenMap={isOpen}
            destination={destination}
            reservedRoomBlocks={reservedRoomBlocks}
            pageTitle={pageTitle}
            onSelectHotel={onSelectHotel}
            isBrannanLayout={isBrannanLayout}
            onToggleFullscreen={onToggleFullscreen}
          />
        )}
      </SkeletonGroup>
      {isBrannanLayout && isMobile && (
        <Flex
          paddingY={6}
          width="100%"
          justifyContent="center"
          position="fixed"
          bottom={0}
          zIndex={1401}
          display={isOpen ? 'none' : 'flex'}
          background="linear-gradient(180deg, rgba(255, 255, 255, 0) 0, #FFF 100%);"
        >
          <ButtonV2 intent="neutral" shape="rounded" backgroundColor="#000000" typographyVariant="button2" startIcon={<MapMarker />} onClick={onOpenMap}>
            Map View
          </ButtonV2>
        </Flex>
      )}
    </Flex>
  );
};
